import { useState, useEffect } from 'react';
import { productsApi } from '../services/crmApi';

export interface Product {
  id: number;
  code: string;
  name: string;
  short_name: string;
  icon: string;
  color: string;
  gradient: string;
  category: string;
  is_active: boolean;
  sort_order: number;
  crm_route_base: string;
  crm_status_field: string;
  features: Record<string, boolean>;
  config: Record<string, any>;
}

export interface PipelineStage {
  id: number;
  product_code: string;
  stage_code: string;
  label: string;
  color: string;
  sort_order: number;
  is_terminal: boolean;
  is_success: boolean;
  allowed_transitions: string[];
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const STAGES_CACHE_MAX = 30; // max entries before FIFO eviction

let productsCache: Product[] | null = null;
let productsCacheTs = 0; // timestamp of last fetch

interface StagesEntry { data: PipelineStage[]; ts: number }
const stagesCache = new Map<string, StagesEntry>();

/** Call on logout to prevent stale data across user sessions. */
export function clearProductsCache() {
  productsCache = null;
  productsCacheTs = 0;
  stagesCache.clear();
}

function isProductsCacheValid(): boolean {
  return productsCache !== null && Date.now() - productsCacheTs < CACHE_TTL_MS;
}

function getStagesFromCache(code: string): PipelineStage[] | null {
  const entry = stagesCache.get(code);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) { stagesCache.delete(code); return null; }
  return entry.data;
}

function setStagesCache(code: string, data: PipelineStage[]) {
  // FIFO eviction when map exceeds max size
  if (stagesCache.size >= STAGES_CACHE_MAX) {
    const firstKey = stagesCache.keys().next().value;
    if (firstKey !== undefined) stagesCache.delete(firstKey);
  }
  stagesCache.set(code, { data, ts: Date.now() });
}

export function useProducts() {
  const [products, setProducts] = useState<Product[]>(productsCache || []);
  const [loading, setLoading] = useState(!isProductsCacheValid());

  useEffect(() => {
    if (isProductsCacheValid()) return;
    productsApi.getProducts().then((data: Product[]) => {
      productsCache = data;
      productsCacheTs = Date.now();
      setProducts(data);
      setLoading(false);
    }).catch(err => { console.warn('[useProducts] product list fetch:', err.message); setLoading(false); });
  }, []);

  return { products, loading };
}

export function useProductStages(code: string) {
  const cached = getStagesFromCache(code);
  const [stages, setStages] = useState<PipelineStage[]>(cached || []);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (!code) return;
    if (getStagesFromCache(code)) return;
    productsApi.getProductStages(code).then((data: PipelineStage[]) => {
      setStagesCache(code, data);
      setStages(data);
      setLoading(false);
    }).catch(err => { console.warn('[useProducts] product by code fetch:', err.message); setLoading(false); });
  }, [code]);

  return { stages, loading };
}
